class ChildrenPicker extends HTMLElement {
  constructor() {
    super();

    this.errors = [];
    this.init();
  }

  resolveOptionValues() {
    const resolvedOptionValues = this.variantSelects.reduce((values, variantSelect) => {
      values.push(variantSelect.querySelector(`#Option-${variantSelect.dataset.productId}-1`).value);
      return values;
    }, []);

    document.dispatchEvent(new CustomEvent('product:childrenChanged', {
      detail: { resolvedOptionValues }
    }));
  }

  // Restrict sizes of the other children (e.g. straps) to the ones compatible with the selected variant of a child
  // that lists compatible products (e.g. watch), or, as a fallback, to the ones matching its strap width and connector.
  restrictCompatibleSizes() {
    const sources = Array.from(this.querySelectorAll('select option:checked, input[type="hidden"]'))
      .filter((el) => el.dataset.compatibleIds || el.dataset.strapWidth);
    if (sources.length === 0) return;

    const listedIds = new Set();
    this.querySelectorAll('[data-compatible-ids]').forEach((el) => {
      el.dataset.compatibleIds.split(',').forEach((id) => listedIds.add(id));
    });
    const compatibleIds = new Set(sources.flatMap((el) => (el.dataset.compatibleIds || '').split(',')));

    this.variantSelects.forEach((variantSelect) => {
      const select = variantSelect.querySelector('select');
      if (!select || select.querySelector('option[data-compatible-ids], option[data-strap-width]')) return;

      const options = Array.from(select.options);
      let isAllowed;
      if (options.some((option) => listedIds.has(option.dataset.id))) {
        isAllowed = (option) => compatibleIds.has(option.dataset.id);
      } else {
        const widths = new Set(sources
          .filter((el) => el.dataset.strapWidth && el.dataset.strapConnector === select.dataset.strapConnector)
          .map((el) => el.dataset.strapWidth));
        isAllowed = (option) => widths.has(option.value);
        // Only restrict straps with the same connector, and never leave nothing to choose from
        if (widths.size === 0 || !options.some(isAllowed)) return;
      }

      options.forEach((option) => {
        option.hidden = !isAllowed(option);
        option.disabled = option.hidden || option.classList.contains('sold-out');
      });

      // Nothing to choose from when only one size is left, like children with a single size
      const wrapper = select.closest('.product-form__input');
      if (wrapper) wrapper.hidden = options.filter((option) => !option.hidden).length === 1;

      if (select.selectedOptions[0]?.hidden) {
        const fallback = options.find((option) => !option.hidden && !option.disabled) || options.find((option) => !option.hidden);
        if (fallback) {
          select.value = fallback.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  init() {
    this.variantSelects = Array.from(this.querySelectorAll('variant-selects'));
    this.restrictCompatibleSizes();
    this.resolveOptionValues();

    // The listener that updates the bundle variant is added after this script might have run,
    // so resolve again when the page is parsed to make the form match the sizes shown
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.variantSelects = Array.from(this.querySelectorAll('variant-selects'));
        this.restrictCompatibleSizes();
        this.resolveOptionValues();
      }, { once: true });
    }

    document.addEventListener('product:variantChangeBundle', () => {
      this.restrictCompatibleSizes();
      this.resolveOptionValues();
    });

    // Initialize the modals and drawers again
    Woolman.ModalsAndDrawers.init();

    // Check and update the add to cart button status
    this.checkAddButton();
  }

  checkAddButton() {
    const productForm = document.getElementById(`product-form-${this.dataset.productId}`);
    if (!productForm) return;

    const variantSelects = Array.from(this.querySelectorAll('variant-selects')).map((select) => select.currentVariant);

    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    const addButtonPrice = addButton.querySelector(
        '#cart-button-price'
      )
    if (!addButton) return;
    if (variantSelects.some(variant => !variant.available) || this.errors.length > 0) {
      addButton.setAttribute('disabled', 'disabled');
      addButtonText.textContent = window.variantStrings.soldOut;

      return;
    }

    addButton.removeAttribute('disabled');
    addButtonText.textContent = window.variantStrings.addToCart;
    if(addButtonPrice && regularPrice) {
          addButtonPrice.innerHTML = `&nbsp;- ${regularPrice}`;
        }
  }
}

customElements.define('children-picker', ChildrenPicker);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;

    this.updateOptions();
    this.updateMasterId();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange(e) {
    this.updateOptions();
    this.updateMasterId();
    this.removeErrorMessage();

    this.updateAddBtn();
    if (this.currentVariant) {
      this.renderProductInfo();
      this.updateMedia();
    }

    const event = new CustomEvent('product:variantChangeBundle', {
      detail: {
        eventId: crypto.randomUUID(),
        ...this.currentVariant
      }
    });

    document.dispatchEvent(event);
  }

  updateAddBtn() {
    const variantSelects = Array.from(document.querySelectorAll('variant-selects')).map((select) => select.currentVariant);
    const currentVariantPicker = this.closest('children-picker');
    const childrenPickerid = currentVariantPicker.getAttribute('data-product-id')
    const productForm = document.getElementById(`product-form-${childrenPickerid}`)

    if (!productForm) return;

    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    addButton.removeAttribute('disabled');
    addButtonText.textContent = window.variantStrings.addToCart;
  }

  updateOptions() {
    const sizeSelect = this.querySelector('select');
    this.selectedSize = sizeSelect ? sizeSelect.value : null;
  }

  updateMasterId() {
    if (!this.selectedSize) {
      this.currentVariant = null;
      return;
    }

    // Get the static color from the first variant (all variants share the same color).
    const staticColor = this.getVariantData()[0].option1;

    this.currentVariant = this.getVariantData().find((variant) =>
      variant.option1 === staticColor &&
      variant.option2 === this.selectedSize
    );
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const currentVariantImage = this.closest('.bundle-child')
      .querySelector(`.bundle-child__image[data-variant-id="${this.currentVariant.id}"]`);
    if (!currentVariantImage) return;

    const productImages = this.closest('.bundle-child')
      .querySelectorAll(`.bundle-child__image[data-product-id="${this.dataset.productId}"]`);

    productImages.forEach(image => {
      image.setAttribute('hidden', 'true');
    });

    currentVariantImage.removeAttribute('hidden');
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  async renderProductInfo() {
    const productDetailsElement = document.querySelector(`.product-detail__child[data-product-id="${this.productId}"]`)
      ?.parentElement;

    if (!productDetailsElement) return;

    const response = await fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&view=details-child`);
    productDetailsElement.outerHTML = await response.text();
  }
}

customElements.define('variant-selects', VariantSelects);

class BundlePrice extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('product:parentVariantChanged', this.updateBundlePrice);
  }

  updateBundlePrice (e) {
    const priceElement = document.querySelector('.product-block__price .price');

    if (!priceElement) { return }

    const { compare_at_price, price } = e.detail;

    const regularPrice = compare_at_price && compare_at_price > price
      ? Woolman.Utils.formatMoney(compare_at_price)
      : Woolman.Utils.formatMoney(price)

    const priceItemRegularElements = priceElement.querySelectorAll('.price-item--regular');
    for (const priceItemRegularElement of priceItemRegularElements) {
      priceItemRegularElement.innerHTML = regularPrice;
    }

    const priceItemSaleElement = priceElement.querySelector('.price-item--sale')
    if (priceItemSaleElement) {
      priceItemSaleElement.innerHTML = Woolman.Utils.formatMoney(price);
    }
  }
}

customElements.define('bundle-price', BundlePrice);

const Photoswipe = {
  attempts: 0,

  selectors: {
    images: '.photoswipe__image'
  },

  init: function() {
    document.querySelectorAll('.photoswipe__image').forEach(trigger => {
      trigger.addEventListener('click', this.downloadAndMount.bind(this));
    });
  },

  downloadAndMount: function(evt) {
    if (this.attempts >= 10) { return };
    this.attempts++;
    const existingScript = document.querySelector(`script[src="${theme.assets.photoswipe}"]`);

    // If we are already processing one slider, wait a while and try again.
    if (window.processingPhotoswipe) {
      setTimeout(this.downloadAndMount.bind(this), 200);
      return;
    }

    // Photoswipe script was already found in document, proceed to immediately mount slider
    if (existingScript) {
      this.showZoom(evt);
      return;
    }

    // Photoswipe script was not found in document, download it and wait for it to load, then show zoom
    window.processingPhotoswipe = true;

    const script = document.createElement('script');
    script.setAttribute('defer', 'true');
    script.setAttribute('async', 'true');
    script.src = theme.assets.photoswipe;
    document.body.append(script);

    script.addEventListener('load', () => {
      this.showZoom(evt)
    })
  },

  showZoom: function(evt) {
    const target = evt.currentTarget || evt.target;
    this.targetElement = target.closest('[data-photoswipe-parent]');
    this.items = this.getImageData();

    this.initGallery(this.items, target.dataset.index || 1);
    window.processingPhotoswipe = false;
  },

  getImageData: function() {
    this.images = this.targetElement.querySelectorAll(this.selectors.images)

    const items = [];

    this.images.forEach(el => {
      const item = {
        src: el.getAttribute('data-photoswipe-src'),
        w: parseInt(el.getAttribute('data-photoswipe-width')),
        h: parseInt(el.getAttribute('data-photoswipe-height')),
        initialZoomLevel: 0.5
      }

      items.push(item);
    });

    return items;
  },

  initGallery: function(items, index) {
    const pswpElement = document.querySelectorAll('.pswp')[0];

    const options = {
      allowPanToNext: false,
      captionEl: false,
      closeOnScroll: false,
      counterEl: false,
      history: false,
      index: parseInt(index) - 1,
      pinchToClose: false,
      preloaderEl: false,
      shareEl: false,
      tapToToggleControls: false
    }

    this.gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
    this.gallery.init();
  }
};
Photoswipe.init()

class ProductBuyBar extends HTMLElement {
  constructor() {
    "use strict";
    super();

    let classes = { show: "show" };
    let addToCartContainer = document.querySelector(".add-to-cart-container");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // In view -> Hide bar
            this.classList.remove(classes.show);
            document.dispatchEvent(new CustomEvent('productBuyBar:onHidden'));
          } else if (this.classList.contains('on-load')) {
            // Not in view -> Show bar
            this.classList.add(classes.show);
            document.dispatchEvent(new CustomEvent('productBuyBar:onVisible'));
          } else {
            if (window.scrollY > 200) {
              // Out of view AND scrolled a little from top -> Show bar
              this.classList.add(classes.show);
              document.dispatchEvent(new CustomEvent('productBuyBar:onVisible'));
            }
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(addToCartContainer);

    // This script is loaded in product-template.liquid --> Footer observer after DOM ready
    document.addEventListener("DOMContentLoaded", function () {
      let siteFooter = document.querySelector("footer.footer");
      if (!siteFooter) return;
      observer.observe(siteFooter);
    });
  }
}
customElements.define("product-buy-bar", ProductBuyBar);
